import { memo, useCallback, useEffect, useState } from "react";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import { MuiMaterials } from "../@index.js";
import { createApiEndpoint } from "../index.js";
import { Spinner, getWindowChromeThemeColor, InfoHint } from "./windowChrome/index.js";

/** Pretty-print JSON for the textarea; returns the original text if it is not valid JSON. */
function formatJsonForDisplay(text) {
    const trimmed = (text ?? "").trim();
    if (!trimmed) return "";
    try {
        return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
        return text ?? "";
    }
}

const CONFIG_HELP =
    "This is the site's operational config (JSON), stored on the server. Secrets and connection settings stay on the gateway and are not shown here. Saving updates only this config and is separate from Save Setup in the header. One-shot flags (saveModuleConfig, scanNow, …) stay in this JSON: set them to true (or an array) to run once; the gateway flips them back to false after it runs. showTemputechService true keeps the Windows TemputechService window visible; false hides it in the background (the process keeps running). Linux ignores that key.";

/** Overrides global `button { font-size }` so label/icon stay vertically centered. */
const configActionButtonSx = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.8125rem",
    lineHeight: 1,
    py: "6px",
    px: "12px",
    minHeight: 32,
    "& .MuiButton-startIcon": {
        display: "inline-flex",
        alignItems: "center",
        marginTop: 0,
        marginBottom: 0,
        "& > *:nth-of-type(1)": {
            fontSize: "1.125rem",
        },
    },
};

/**
 * Site Setup → Config Setup: shows the site's server-authoritative operational config (the SiteConfig table) in a
 * text area and lets an admin save it back. Reads/writes the server directly, so it is instant and works even when
 * the site is offline; the gateway (TemputechService) merges the saved config on its next poll. This is separate
 * from Save Setup in the header — saving here only updates the site config, and Save Setup never touches it.
 */
function SetupConfigPanel({ isOpen, onToggle, siteGuid, border, textSecondary, paper }) {
    const [contents, setContents] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState("");
    const [loadedForGuid, setLoadedForGuid] = useState("");

    const loadConfig = useCallback(async () => {
        if (!siteGuid) return;
        setSaving(false);
        setLoading(true);
        setStatus("");
        try {
            const text = await createApiEndpoint().fetchSiteConfig("direct", siteGuid);
            setContents(formatJsonForDisplay(text));
            setLoadedForGuid(siteGuid);
            setStatus(text && text.length > 0
                ? ""
                : "No server config for this site yet. It will populate after the site checks in, or you can paste a config and Save.");
        } catch (err) {
            setStatus(`Error loading config: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [siteGuid]);

    // Auto-load when the section is first opened for a site.
    useEffect(() => {
        if (isOpen && siteGuid && loadedForGuid !== siteGuid && !loading) {
            loadConfig();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, siteGuid]);

    // Reset when switching sites so a stale config is never shown.
    useEffect(() => {
        setContents("");
        setStatus("");
        setLoadedForGuid("");
    }, [siteGuid]);

    const handleSave = useCallback(async () => {
        if (!siteGuid) return;
        // Guard against saving invalid JSON that the gateway would reject.
        if (contents && contents.trim().length > 0) {
            try {
                JSON.parse(contents);
            } catch (e) {
                setStatus(`Not saved \u2014 the config is not valid JSON: ${e.message}`);
                return;
            }
        }
        setSaving(true);
        setStatus("Saving the site config\u2026");
        try {
            const ok = await createApiEndpoint().saveSiteConfig("direct", siteGuid, contents);
            setStatus(ok
                ? "Config saved. The site will apply it on its next check-in (usually within a minute). One-shot flags flip back to false after they run; Reload to see that."
                : "Failed to save the config. You may not have permission.");
        } catch (err) {
            setStatus(`Error saving config: ${err.message}`);
        } finally {
            setSaving(false);
        }
    }, [siteGuid, contents]);

    return (
        <MuiMaterials.Paper
            elevation={0}
            className={`setup-section${isOpen ? " setup-section--open" : ""}`}
            sx={{
                backgroundColor: isOpen
                    ? getWindowChromeThemeColor("#e8e8e8", "#1e1e1e")
                    : "transparent",
                border: "none",
                borderRadius: 0,
                mb: 0,
                overflow: "hidden",
                boxShadow: "none",
            }}
        >
            <MuiMaterials.Box
                sx={{
                    px: { xs: 1.5, sm: 1.75 },
                    py: 1.25,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    userSelect: "none",
                }}
                onClick={onToggle}
            >
                <MuiMaterials.ExpandMoreIcon
                    sx={{
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s",
                    }}
                />
                <MuiMaterials.Typography fontWeight={600} sx={{ fontSize: { xs: 15, sm: 16 } }}>
                    Config
                </MuiMaterials.Typography>
            </MuiMaterials.Box>
            {isOpen ? (
                <MuiMaterials.Box
                    sx={{
                        px: 2,
                        pt: 1.5,
                        pb: 2,
                        backgroundColor: getWindowChromeThemeColor("#e8e8e8", "#1e1e1e"),
                    }}
                >
                    <MuiMaterials.Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            startIcon={loading ? <Spinner size="tiny" /> : <RefreshIcon />}
                            onClick={loadConfig}
                            disabled={loading || saving || !siteGuid}
                            sx={configActionButtonSx}
                        >
                            {loading ? "Loading\u2026" : "Reload"}
                        </MuiMaterials.Button>
                        <MuiMaterials.Button
                            size="small"
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving || loading || !siteGuid}
                            title="Saves only this config. Does not save site setup."
                            sx={configActionButtonSx}
                        >
                            {saving ? "Saving\u2026" : "Save Config"}
                        </MuiMaterials.Button>
                        <InfoHint title={CONFIG_HELP} ariaLabel="Site config help" />
                    </MuiMaterials.Stack>
                    {status ? (
                        <MuiMaterials.Typography variant="body2" sx={{ color: textSecondary, mb: 1 }}>
                            {status}
                        </MuiMaterials.Typography>
                    ) : null}
                    <MuiMaterials.TextField
                        value={contents}
                        onChange={(e) => setContents(e.target.value)}
                        multiline
                        minRows={18}
                        maxRows={40}
                        fullWidth
                        placeholder="The site's operational config (JSON) will appear here."
                        InputProps={{
                            sx: {
                                fontFamily: "monospace",
                                fontSize: 12,
                                backgroundColor: paper,
                                alignItems: "flex-start",
                            },
                        }}
                    />
                </MuiMaterials.Box>
            ) : null}
        </MuiMaterials.Paper>
    );
}

export default memo(SetupConfigPanel);
